import { Flex, Select } from 'antd';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Col, Table } from 'react-bootstrap';
import IconButton from '../common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import AppLoader from '../common/AppLoader';

const columnTypesOptions = [
  { label: 'String', value: 'String' },
  { label: 'Varchar', value: 'Varchar' },
  { label: 'Number', value: 'Number' },
  { label: 'Int', value: 'Int' },
  { label: 'Boolean', value: 'Boolean' },
  { label: 'Double', value: 'Double' },
  { label: 'Float', value: 'Float' },
  { label: 'Date', value: 'Date' },
];

type Props = {
  children?: React.ReactNode;
  columns?: any[];
  onColumnsChange: (columns: any[]) => void;
}

export default function ColumnsEditor({ children, columns, onColumnsChange }: Props) {
  const [editingColumns, setEditingColumns] = useState<any[]>();
  const [originalColumns, setOriginalColumns] = useState<any[]>();
  const [editing, setEditing] = useState<number>();
  const [editingColumn, setEditingColumn] = useState<any>();

  useEffect(() => {
    setEditing(undefined);
    setEditingColumns(columns);
    setOriginalColumns(columns);
  }, [columns]);

  const handleColumnTypeChange = useCallback((value: string) => {
    setEditingColumn((curr: any) => ({
      ...curr!,
      type: value,
    }));
  }, []);

  const revertEdit = useCallback((index: number) => () => {
    setEditingColumns(curr => {
      const newColumns = cloneDeep(curr!);
      newColumns[index].edited = false;
      newColumns[index] = originalColumns![index];
      onColumnsChange?.(newColumns);
      return newColumns;
    });
  }, [onColumnsChange, originalColumns]);

  const editColumn = useCallback((index: number) => () => {
    setEditing(index);
    setEditingColumn(cloneDeep(editingColumns![index]));
  }, [editingColumns]);

  const cancelEdit = useCallback(() => {
    setEditing(undefined);
    setEditingColumn(null);
  }, []);

  const confirmEdit = useCallback(() => {
    setEditingColumns(curr => {
      const newColumns = cloneDeep(curr!);
      newColumns[editing!] = editingColumn;
      if (editingColumn.type !== originalColumns![editing!].type) newColumns[editing!].edited = true;
      else newColumns[editing!].edited = false;
      onColumnsChange?.(newColumns);
      return newColumns;
    });
    cancelEdit();
  }, [cancelEdit, editingColumn, originalColumns, onColumnsChange, editing]);

  return (
    <div className="mt-3">
      <Flex align="center" className="mb-2">
        <Col>
          <h6 className="mb-0">Table Schema</h6>
        </Col>
        {children}
      </Flex>
      <div className="list-wrapper">
        <Table className="table-schema" bordered>
          <thead className="bg-light">
            <tr>
              <th>Name</th>
              <th className="text-center" style={{ width: "40%" }}>Type</th>
              <th style={{ width: "100px" }}></th>
            </tr>
          </thead>
          <tbody>
            {!editingColumns && (
              <tr>
                <td colSpan={3}>
                  <AppLoader justify="center" size={100} />
                </td>
              </tr>
            )}

            {editingColumns?.length === 0 && (
              <tr>
                <td colSpan={3}>
                  <Alert variant="info" className="py-2">
                    <FontAwesomeIcon icon={solid('info-circle')} />
                    <span className="ms-2">No columns found.</span>
                  </Alert>
                </td>
              </tr>
            )}

            {editingColumns?.map((col, index) => (
              <tr key={col.name} className={classNames({ edit: editing === index, edited: col.edited })}>
                <td>{col.name}</td>
                <td className="text-center">
                  {editing !== index && <small className="text-muted text-uppercase">{col.type}</small>}
                  {editing === index && (
                    <Select
                      placeholder="Select column type"
                      className="w-100"
                      onChange={handleColumnTypeChange}
                      options={columnTypesOptions}
                      value={editingColumn.type}
                    />
                  )}
                </td>
                <td className="text-center">
                  {editing !== index && (
                    <>
                      {col.edited && (
                        <IconButton type="button" className="p-1" onClick={revertEdit(index)} tooltip="Revert">
                          <FontAwesomeIcon icon={solid('undo')} />
                        </IconButton>
                      )}
                      <IconButton type="button" className="p-1" onClick={editColumn(index)} tooltip="Edit">
                        <FontAwesomeIcon icon={solid('edit')} />
                      </IconButton>
                    </>
                  )}
                  {editing === index && (
                    <>
                      <IconButton type="button" className="text-success p-1 me-2" onClick={confirmEdit} tooltip="Confirm">
                        <FontAwesomeIcon icon={solid('check')} />
                      </IconButton>
                      <IconButton type="button" className="text-danger p-1" onClick={cancelEdit} tooltip="Cancel">
                        <FontAwesomeIcon icon={solid('times')} />
                      </IconButton>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  )
}